import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box, Card, CardContent, Typography, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button,
  TextField, InputAdornment, FormControl, InputLabel, Select,
  MenuItem, Chip, IconButton, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Drawer, Avatar, List, ListItem, ListItemAvatar, ListItemText,
  Divider, Badge, LinearProgress,
} from '@mui/material';
import {
  Search, Delete, PlayArrow, Stop, Add,
  Casino, Group, Timer, People, Close,
  Refresh, CreditCard, CheckCircle, Cancel, FiberManualRecord,
} from '@mui/icons-material';
import { RootState } from '../store';
import {
  fetchRooms, forceDeleteRoom, forceStartRoom, forceEndRoom, clearFilters,
} from '../store/slices/roomsSlice';
import { Room, RoomStatus, RoomStake, RoomFilters, RoomPlayerDetail } from '../types';
import dayjs from 'dayjs';
import RoomFormModal from '../components/rooms/RoomFormModal';
import { apiService } from '../services/api';
import socketService from '../services/socket';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('en-ET', { style: 'currency', currency: 'ETB' }).format(n);

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  waiting: 'default', countdown: 'warning', playing: 'info',
  shuffling: 'warning', finished: 'success',
};

const STAKE_OPTIONS: RoomStake[] = [5, 10, 20, 30, 50, 100, 200, 500, 1000];

// ─── Players Drawer ──────────────────────────────────────────────────────────
interface PlayersDrawerProps {
  room: Room | null;
  open: boolean;
  onClose: () => void;
}

const PlayersDrawer: React.FC<PlayersDrawerProps> = ({ room, open, onClose }) => {
  const [players, setPlayers]       = useState<RoomPlayerDetail[]>([]);
  const [loading, setLoading]       = useState(false);
  const [meta, setMeta]             = useState({ totalPlayers: 0, playersWithCards: 0 });
  const [liveStatus, setLiveStatus] = useState<string>('');
  const [lastEvent, setLastEvent]   = useState<string>('');
  const roomIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!room) return;
    setLoading(true);
    try {
      const res = await apiService.getRoomPlayers(room._id);
      setPlayers(res.players || []);
      setMeta({ totalPlayers: res.totalPlayers, playersWithCards: res.playersWithCards });
      setLiveStatus(res.status);
    } catch {
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [room]);

  useEffect(() => {
    if (!open || !room) { setPlayers([]); roomIdRef.current = null; return; }
    roomIdRef.current = room._id;
    load();

    const handleJoined = (data: any) => {
      if (data.roomId?.toString() !== roomIdRef.current) return;
      setLastEvent(`${data.username} joined`);
      setMeta(m => ({ ...m, totalPlayers: data.currentPlayers }));
      load();
    };
    const handleLeft = (data: any) => {
      if (data.roomId?.toString() !== roomIdRef.current) return;
      setLastEvent(`${data.username} left`);
      setMeta(m => ({ ...m, totalPlayers: data.currentPlayers }));
      load();
    };
    const handleUpdated = (data: any) => {
      if (data.roomId?.toString() !== roomIdRef.current) return;
      setLiveStatus(data.status);
      setLastEvent(`Status → ${data.status}`);
      setMeta(m => ({ ...m, totalPlayers: data.currentPlayers }));
    };

    socketService.onGlobalRoomJoined(handleJoined);
    socketService.onGlobalRoomLeft(handleLeft);
    socketService.onGlobalRoomUpdated(handleUpdated);
    return () => {
      socketService.offGlobalRoomJoined(handleJoined);
      socketService.offGlobalRoomLeft(handleLeft);
      socketService.offGlobalRoomUpdated(handleUpdated);
    };
  }, [open, room, load]);

  const cardPct = meta.totalPlayers > 0
    ? Math.round((meta.playersWithCards / meta.totalPlayers) * 100) : 0;
  const displayStatus = liveStatus || room?.status || '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '100vw', md: 440 },
          maxWidth: '100vw',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg,#667eea,#764ba2)',
        color: 'white',
        flexShrink: 0,
      }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Room Players
          </Typography>
          {room && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Stake: {fmt(room.stake)}
              </Typography>
              <Chip
                label={displayStatus}
                size="small"
                color={STATUS_COLOR[displayStatus] || 'default'}
                sx={{ height: 18, fontSize: 10 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <FiberManualRecord sx={{ fontSize: 9, color: '#4caf50' }} />
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 10 }}>LIVE</Typography>
              </Box>
            </Box>
          )}
          {lastEvent && (
            <Typography variant="caption" sx={{
              opacity: 0.75, fontSize: 10, display: 'block', mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              ↳ {lastEvent}
            </Typography>
          )}
        </Box>
        {/* Action buttons — always visible, no overflow */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, mt: 0.25 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} sx={{ color: 'white', p: 0.75 }}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white', p: 0.75 }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Summary cards ── */}
      <Box sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          {[
            { label: 'Players',  value: meta.totalPlayers,                         color: '#667eea' },
            { label: 'Cards',    value: meta.playersWithCards,                     color: '#11998e' },
            { label: 'No Card',  value: meta.totalPlayers - meta.playersWithCards, color: '#f5a623' },
          ].map(s => (
            <Box
              key={s.label}
              sx={{
                flex: 1,
                borderRadius: 2,
                p: { xs: '8px 10px', sm: '10px 14px' },
                bgcolor: s.color,
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1 }}>
                {s.value}
              </Typography>
              <Typography sx={{ fontSize: { xs: 9, sm: 11 }, opacity: 0.9, mt: 0.25 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Card purchase rate bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', fontSize: { xs: 10, sm: 12 } }}>
            Card rate
          </Typography>
          <LinearProgress
            variant="determinate"
            value={cardPct}
            sx={{ flex: 1, height: 7, borderRadius: 4 }}
            color={cardPct === 100 ? 'success' : cardPct > 50 ? 'primary' : 'warning'}
          />
          <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontSize: { xs: 10, sm: 12 } }}>
            {cardPct}%
          </Typography>
        </Box>
      </Box>

      {/* ── Player list ── */}
      <Box sx={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={36} />
          </Box>
        ) : players.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 6, px: 2, color: 'text.secondary' }}>
            <People sx={{ fontSize: 52, opacity: 0.25, mb: 1 }} />
            <Typography variant="body2">No players in this room yet</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {players.map((p, i) => (
              <React.Fragment key={p.userId}>
                {/* 
                  Use a custom row instead of ListItem + secondaryAction.
                  secondaryAction uses absolute positioning which overlaps
                  content on narrow screens. A flex row is fully responsive.
                */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1, sm: 1.25 },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {/* Avatar with card badge */}
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      p.hasPurchasedCard
                        ? <CheckCircle sx={{ fontSize: 13, color: 'success.main', bgcolor: 'background.paper', borderRadius: '50%' }} />
                        : <Cancel sx={{ fontSize: 13, color: 'warning.main', bgcolor: 'background.paper', borderRadius: '50%' }} />
                    }
                  >
                    <Avatar sx={{
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      fontSize: { xs: 14, sm: 16 },
                      fontWeight: 700,
                      flexShrink: 0,
                      bgcolor: `hsl(${(i * 47) % 360},60%,45%)`,
                    }}>
                      {(p.username || '?')[0].toUpperCase()}
                    </Avatar>
                  </Badge>

                  {/* Name + telegram — takes remaining space, truncates */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.username}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: { xs: 10, sm: 11 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {p.telegramUsername ? `@${p.telegramUsername}` : p.telegramId || '—'}
                    </Typography>
                  </Box>

                  {/* Wallet + card chip — fixed width, never wraps */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: 'success.main',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmt(p.wallet)}
                    </Typography>
                    {p.hasPurchasedCard ? (
                      <Chip
                        icon={<CreditCard sx={{ fontSize: '11px !important' }} />}
                        label={`#${p.cardId}`}
                        size="small"
                        color="success"
                        sx={{ fontSize: { xs: 9, sm: 10 }, height: 18, mt: 0.5 }}
                      />
                    ) : (
                      <Chip
                        label="No card"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: { xs: 9, sm: 10 }, height: 18, mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Box>
                {i < players.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const RoomsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { rooms, statistics, loading } = useSelector((state: RootState) => state.rooms);

  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState<RoomStatus | ''>('');
  const [stakeFilter, setStakeFilter]   = useState<RoomStake | ''>('');
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [playersRoom, setPlayersRoom]   = useState<Room | null>(null);
  const [showPlayers, setShowPlayers]   = useState(false);

  // Live player counts per room (updated via socket)
  const [liveCounts, setLiveCounts] = useState<Record<string, { count: number; status: string }>>({});

  useEffect(() => { dispatch(fetchRooms() as any); }, [dispatch]);

  // ── Socket: update player counts + status in the table live ──────────────
  useEffect(() => {
    const handleJoined = (data: any) => {
      setLiveCounts(prev => ({
        ...prev,
        [data.roomId]: { count: data.currentPlayers, status: data.status },
      }));
    };
    const handleLeft = (data: any) => {
      setLiveCounts(prev => ({
        ...prev,
        [data.roomId]: { count: data.currentPlayers, status: data.status },
      }));
    };
    const handleUpdated = (data: any) => {
      setLiveCounts(prev => ({
        ...prev,
        [data.roomId]: { count: data.currentPlayers, status: data.status },
      }));
    };

    socketService.onGlobalRoomJoined(handleJoined);
    socketService.onGlobalRoomLeft(handleLeft);
    socketService.onGlobalRoomUpdated(handleUpdated);

    return () => {
      socketService.offGlobalRoomJoined(handleJoined);
      socketService.offGlobalRoomLeft(handleLeft);
      socketService.offGlobalRoomUpdated(handleUpdated);
    };
  }, []);

  const handleSearch = () => {
    const filters: RoomFilters = {};
    if (statusFilter) filters.status = statusFilter;
    if (stakeFilter)  filters.stake  = stakeFilter;
    dispatch(fetchRooms(filters) as any);
  };

  const handleViewPlayers = (room: Room) => {
    setPlayersRoom(room);
    setShowPlayers(true);
  };

  const handleDeleteRoom = (room: Room) => {
    setSelectedRoom(room);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRoom) return;
    setDeleteLoading(true);
    try {
      const result = await dispatch(forceDeleteRoom(selectedRoom._id) as any);
      if (result.payload?.success) {
        toast.success('Room deleted successfully');
        setShowDeleteDialog(false);
        dispatch(fetchRooms() as any);
      }
    } catch { toast.error('Failed to delete room'); }
    finally { setDeleteLoading(false); }
  };

  const handleForceStart = async (roomId: string) => {
    try {
      const r = await dispatch(forceStartRoom({ roomId, reason: 'Admin force start' }) as any);
      if (r.payload?.success) { toast.success('Room started'); dispatch(fetchRooms() as any); }
    } catch { toast.error('Failed to start room'); }
  };

  const handleForceEnd = async (roomId: string) => {
    try {
      const r = await dispatch(forceEndRoom({ roomId, reason: 'Admin force end' }) as any);
      if (r.payload?.success) { toast.success('Room ended'); dispatch(fetchRooms() as any); }
    } catch { toast.error('Failed to end room'); }
  };

  // Merge live socket data with Redux room data
  const getDisplayCount  = (room: Room) => liveCounts[room._id]?.count  ?? room.players?.length ?? 0;
  const getDisplayStatus = (room: Room) => (liveCounts[room._id]?.status ?? room.status) as RoomStatus;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Rooms Management
      </Typography>

      {/* Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        {[
          { label: 'Total Rooms',   value: statistics.totalRooms,   icon: <Casino />,    color: 'primary.main' },
          { label: 'Active Rooms',  value: statistics.activeRooms,  icon: <PlayArrow />, color: 'success.main' },
          { label: 'Waiting Rooms', value: statistics.waitingRooms, icon: <Timer />,     color: 'warning.main' },
          { label: 'Playing Rooms', value: statistics.playingRooms, icon: <Group />,     color: 'info.main'    },
        ].map(s => (
          <Box key={s.label} sx={{ flex: '1 1 140px', minWidth: 120 }}>
            <Card>
              <CardContent sx={{ py: '12px !important' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ color: s.color }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1 }}>{s.value}</Typography>
                    <Typography variant="caption" color="textSecondary">{s.label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            placeholder="Search rooms..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            size="small" sx={{ flex: '1 1 200px' }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value as RoomStatus | '')} label="Status">
              <MenuItem value="">All</MenuItem>
              {['waiting','countdown','shuffling','playing','finished'].map(s => (
                <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
            <InputLabel>Stake</InputLabel>
            <Select value={stakeFilter} onChange={e => setStakeFilter(e.target.value as RoomStake | '')} label="Stake">
              <MenuItem value="">All</MenuItem>
              {STAKE_OPTIONS.map(s => <MenuItem key={s} value={s}>{fmt(s)}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={handleSearch} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Search />}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={() => {
            setSearchTerm(''); setStatusFilter(''); setStakeFilter('');
            dispatch(clearFilters()); dispatch(fetchRooms() as any);
          }}>Clear</Button>
          <Button variant="contained" color="primary" size="small" startIcon={<Add />}
            onClick={() => setShowCreateModal(true)} sx={{ width: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' } }}>
            Create Room
          </Button>
        </Box>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{ minWidth: { xs: 520, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Stake</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Players</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Countdown</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms && rooms.length > 0 ? rooms.map(room => {
                const displayCount  = getDisplayCount(room);
                const displayStatus = getDisplayStatus(room);
                const isLive = !!liveCounts[room._id];
                return (
                  <TableRow key={room._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{fmt(room.stake)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View players">
                        <Box
                          onClick={() => handleViewPlayers(room)}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.75,
                            cursor: 'pointer', borderRadius: 1, px: 0.75, py: 0.25,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <People fontSize="small" color="primary" />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {displayCount}
                          </Typography>
                          {isLive && (
                            <FiberManualRecord sx={{ fontSize: 8, color: '#4caf50' }} />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                        color={STATUS_COLOR[displayStatus] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {room.countdown > 0
                        ? <Box display="flex" alignItems="center" gap={0.5}><Timer fontSize="small" /><Typography variant="body2">{room.countdown}s</Typography></Box>
                        : <Typography variant="body2" color="textSecondary">—</Typography>
                      }
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {room.createdAt ? dayjs(room.createdAt).format('MMM DD, HH:mm') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="View Players">
                          <IconButton size="small" color="primary" onClick={() => handleViewPlayers(room)}>
                            <People fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {displayStatus === 'waiting' && (
                          <Tooltip title="Force Start">
                            <IconButton size="small" color="success" onClick={() => handleForceStart(room._id)}>
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(displayStatus === 'playing' || displayStatus === 'countdown') && (
                          <Tooltip title="Force End">
                            <IconButton size="small" color="warning" onClick={() => handleForceEnd(room._id)}>
                              <Stop fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Room">
                          <IconButton size="small" color="error" onClick={() => handleDeleteRoom(room)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      {loading ? 'Loading rooms...' : 'No rooms found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Players Drawer */}
      <PlayersDrawer room={playersRoom} open={showPlayers} onClose={() => setShowPlayers(false)} />

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Room</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            Delete room with stake <strong>{selectedRoom && fmt(selectedRoom.stake)}</strong>? This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : undefined}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Room Modal */}
      <RoomFormModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchRooms() as any)}
      />
    </Box>
  );
};

export default RoomsPage;
